import {
  ExternalLinkIcon,
  ServerOffIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { FRONTEND_ROUTES } from "@/constants"
import { useMerchantMtlsCertificates } from "@/services/admin/merchant-mtls-certificates"

const prefix =
  "pages.adminMerchantProcessors.detail.certificateSelect"

interface ProcessorCertificateSelectProps {
  /** Entrada do cofre em uso (`MTC-...`); ausente = credencial sem certificado. */
  value?: string | null
  onChange: (certificateId: string) => void
  disabled?: boolean
}

/**
 * Escolhe qual certificado do cofre esta CREDENCIAL usa.
 *
 * Existe separado do `CertificateVaultSelect` por causa de quem é o dono: aquele lê o cofre do
 * white label de uma configuração; a credencial é recurso do MERCHANT, então o cofre certo é o do
 * merchant do token — pedir um white label aqui seria inventar um dono que a credencial não tem.
 *
 * Fica fora do `ProviderForm` porque ele filtra o campo `CERTIFICATE_REF` de propósito: um uuid
 * solto no meio das credenciais não diz nada. Aqui a escolha vem com validade, emissor e o atalho
 * para o cofre, que é o contexto que dá sentido a ela.
 */
export const ProcessorCertificateSelect = ({
  value,
  onChange,
  disabled,
}: ProcessorCertificateSelectProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useMerchantMtlsCertificates()

  const entries = Array.isArray(data) ? data : []

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor="processor-certificate">
          {t(`${prefix}.label`)}
        </Label>
        <Button
          asChild
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
        >
          <Link
            to={`${FRONTEND_ROUTES.ADMIN_MERCHANT.href}?tab=mtlsCertificates`}
          >
            {t(`${prefix}.manage`)}
            <ExternalLinkIcon
              className="ml-1 size-3"
              aria-hidden="true"
            />
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-9 w-full" />
      ) : isError ? (
        /* Erro não é "cofre vazio": dizer que não há certificado quando a consulta caiu levaria
           o operador a cadastrar de novo um que já existe. */
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 p-2">
          <ServerOffIcon
            className="size-4 text-red-700"
            aria-hidden="true"
          />
          <p
            role="status"
            aria-live="polite"
            className="text-xs text-red-700"
          >
            {t(`${prefix}.loadError`)}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => refetch()}
          >
            {t(`${prefix}.retry`)}
          </Button>
        </div>
      ) : (
        <Select
          value={value ?? ""}
          disabled={disabled}
          onValueChange={onChange}
        >
          <SelectTrigger id="processor-certificate">
            <SelectValue
              placeholder={t(`${prefix}.placeholder`)}
            />
          </SelectTrigger>
          <SelectContent>
            {entries.length === 0 && (
              <p
                role="status"
                aria-live="polite"
                className="px-2 py-3 text-xs text-muted-foreground"
              >
                {t(`${prefix}.empty`)}
              </p>
            )}
            {entries.map((entry) => (
              <SelectItem key={entry.id} value={entry.id}>
                {/* Nome + validade: o uuid sozinho não deixa escolher entre duas entradas do
                    mesmo emissor — e é a validade que decide qual delas serve. */}
                {entry.name ?? entry.id}
                {entry.certificate?.notAfter
                  ? ` · ${t(`${prefix}.validUntil`, { date: entry.certificate.notAfter })}`
                  : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <p className="text-xs text-muted-foreground">
        {t(`${prefix}.hint`)}
      </p>
    </div>
  )
}
